<template>
  <DarkFull>
    <div class="min-h-full flex flex-col bg-gray-950">
      <div
        class="w-full h-16 border-b border-gray-900 flex items-center justify-center"
      >
        <img class="mx-auto h-6 w-auto" :src="Logo" :alt="$t('account.logo_alt')" />
      </div>
      <div class="w-full flex-1 items-center justify-center flex flex-col">
        <div class="sm:mx-auto sm:w-full sm:max-w-md bg-gray-900 rounded-lg">
          <div class="py-8 px-4 shadow sm:rounded-md sm:px-6">
            <form
              @submit.prevent="loginRedirect"
              class="space-y-6 border-b border-gray-800 pb-6"
            >
              <div
                class="flex flex-col w-full items-center border-b border-gray-800 pb-6"
              >
                <h1 class="text-text-primary font-semibold text-[24px]">
                  {{ $t('account.login.title') }}
                </h1>
                <h2 class="text-text-secondary">
                  {{ $t('account.login.subtitle') }}
                  <span v-if="$route.query.join" class="block text-center text-primary-300">
                    {{ $t('play.lan_party.login_context', { name: $route.query.join }) }}
                  </span>
                </h2>
              </div>

              <div>
                <label
                  for="email"
                  class="block text-sm font-medium text-gray-100"
                >
                  {{ $t('account.fields.email') }}
                </label>
                <div class="mt-2">
                  <input
                    v-model="state.form.body.email"
                    id="email"
                    name="email"
                    type="email"
                    autofocus
                    tabindex="1"
                    autocomplete="email"
                    :placeholder="$t('account.fields.email_placeholder')"
                    required="true"
                    class="text-text-primary placeholder:text-text-secondary bg-gray-800 appearance-none block w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-200 focus:outline-none focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                  />
                </div>
                <div class="text-danger text-sm">
                  {{ state.form.errors.email }}
                </div>
              </div>

              <div>
                <div class="flex">
                  <label
                    for="password"
                    class="block text-sm font-medium text-gray-100 flex-1"
                  >
                    {{ $t('account.fields.password') }}
                  </label>
                  <router-link
                    to="/reset_password"
                    class="text-xs text-primary-400 hover:text-primary-300"
                  >
                    {{ $t('account.login.forgot_password') }}
                  </router-link>
                </div>

                <div class="mt-2">
                  <input
                    v-model="state.form.body.password"
                    id="password"
                    name="password"
                    type="password"
                    tabindex="2"
                    autocomplete="current-password"
                    :placeholder="$t('account.login.password_placeholder')"
                    required="true"
                    class="text-text-primary placeholder:text-text-secondary bg-gray-800 appearance-none block w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-200 focus:outline-none focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                  />
                </div>
                <div class="text-danger text-sm">
                  {{ state.form.errors.password }}
                </div>
              </div>

              <div v-if="state.form.codeRequired">
                <label
                  for="login-code"
                  class="block text-sm font-medium text-gray-100"
                >
                  {{ $t('account.login.code_label') }}
                </label>
                <p class="text-xs text-text-secondary mt-1">
                  {{ $t('account.login.code_hint') }}
                </p>
                <div class="mt-2">
                  <input
                    v-model="state.form.body.login_code"
                    id="login-code"
                    name="login-code"
                    type="text"
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    maxlength="6"
                    pattern="[0-9]{6}"
                    placeholder="123456"
                    required="true"
                    class="text-text-primary placeholder:text-text-secondary bg-gray-800 appearance-none block w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-200 focus:outline-none focus:ring-gray-400 focus:border-gray-400 sm:text-sm"
                  />
                </div>
              </div>

              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <input
                    id="remember-me"
                    v-model="state.form.body.remember"
                    name="remember-me"
                    type="checkbox"
                    tabindex="3"
                    class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    for="remember-me"
                    class="ml-2 block text-sm text-gray-100"
                  >
                    {{ $t('account.login.remember_me') }}
                  </label>
                </div>
              </div>

              <div>
                <div>
                  <button
                    type="submit"
                    tabindex="4"
                    class="w-full flex justify-center py-3 px-4 border border-transparent duration-300 rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {{ $t('account.login.submit') }}
                    <!-- spinner when clickked -->
                    <svg
                      v-if="loading"
                      class="animate-spin -mr-1 ml-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                      />
                      <path
                        class="opacity-75"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                </div>
                <div class="relative flex py-5 items-center">
                  <div class="flex-grow border-t border-gray-800" />
                  <span
                    class="flex-shrink mx-4 text-text-secondary uppercase font-light text-sm tracking-[1.5px]"
                  >{{ $t('account.login.or') }}</span
                  >
                  <div class="flex-grow border-t border-gray-800" />
                </div>

                <div>
                  <form
                    action="https://steamcommunity.com/openid/login"
                    method="get"
                  >
                    <input
                      type="hidden"
                      name="openid.identity"
                      value="http://specs.openid.net/auth/2.0/identifier_select"
                    />
                    <input
                      type="hidden"
                      name="openid.claimed_id"
                      value="http://specs.openid.net/auth/2.0/identifier_select"
                    />
                    <input
                      type="hidden"
                      name="openid.ns"
                      value="http://specs.openid.net/auth/2.0"
                    />
                    <input
                      type="hidden"
                      name="openid.mode"
                      value="checkid_setup"
                    />
                    <input
                      type="hidden"
                      name="openid.return_to"
                      :value="getSteamAuthURL()"
                    />
                    <input
                      type="hidden"
                      name="openid.realm"
                      :value="getSteamAuthURL()"
                    />
                    <button
                      type="submit"
                      class="justify-center items-center w-full py-3 flex gap-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white duration-300 bg-gray-800 hover:bg-gray-700 focus:outline-none"
                    >
                      <img
                        class="w-4 h-4"
                        src="../assets/icons/steam-icon.svg"
                      />
                      {{ $t('account.login.with_steam') }}
                    </button>
                  </form>
                </div>
              </div>
              <div>
                <i18n-t scope="global" keypath="account.login.accept_terms" tag="p" class="text-text-secondary text-xs text-center">
                  <template #br><br /></template>
                  <template #privacy_policy>
                    <a
                      target="_blank"
                      class="text-primary-400"
                      href="https://wiki.refrag.gg/privacy-policy"
                    >{{ $t('account.privacy_policy') }}</a
                    >
                  </template>
                  <template #terms_of_service>
                    <a
                      target="_blank"
                      class="text-primary-400"
                      href="https://wiki.refrag.gg/terms-of-service"
                    >{{ $t('account.terms_of_service') }}</a
                    >
                  </template>
                </i18n-t>
              </div>
            </form>
            <div class="flex gap-2 w-full items-center justify-center pt-6">
              <h1 class="text-text-primary text-sm">{{ $t('account.login.no_account') }}</h1>
              <a
                @click="navToRegister()"
                class="text-primary-400 underline text-xs cursor-pointer hover:text-primary-300"
              >{{ $t('account.login.sign_up_here') }}</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </DarkFull>
</template>

<script>

import { useAuth } from '@websanova/vue-auth/src/v3.js';
import {
  reactive, onMounted, getCurrentInstance, watch,
} from 'vue';
import { mapMutations } from 'vuex';
import { useHead } from '@unhead/vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import DarkFull from '../components/layouts/DarkFull.vue';
import Logo from '../utils/logo';
import { signInStarted, trackEvent } from '../utils/analytics';
import {
  getEnvVar,
  getSteamAuthURL as buildSteamAuthURL,
} from '../utils/steam_openid';

export default {
  setup() {
    const auth = useAuth();
    const { proxy } = getCurrentInstance();
    const cookies = proxy.$cookies;
    const router = useRouter();
    const route = useRoute();
    const { t } = useI18n();

    const state = reactive({
      form: {
        body: {
          email: '',
          password: '',
          login_code: '',
        },
        // Set when the backend withholds the token until the emailed code is entered.
        codeRequired: false,
        remember: false,
        fetchUser: true,
        staySignedIn: true,
        errors: {},
      },
    });

    // A code belongs to the account that asked for it.
    watch(
      () => state.form.body.email,
      () => {
        state.form.codeRequired = false;
        state.form.body.login_code = '';
      },
    );

    useHead({
      title: t('account.login.page_title'),
      htmlAttrs: {
        lang: 'en',
        amp: true,
      },
    });

    onMounted(() => {
      const trialSource = extractClaimTrialSource(route.redirectedFrom);
      persistTrialCookie(trialSource);

      if (auth.redirect()?.from?.query.info) {
        if (cookies.get('has_logged_in') !== 'true') {
          window.location.href = 'https://refrag.gg';
        }
      }
    });

    function extractClaimTrialSource(targetRoute) {
      const sourceParam = targetRoute?.params?.source;
      if (typeof sourceParam === 'string' && sourceParam.trim()) {
        return sourceParam.trim();
      }

      const path = targetRoute?.path || targetRoute?.fullPath || '';
      const match = path.match(/^\/claim_trial\/([^/?#]+)/);

      if (!match?.[1]) return null;

      return decodeURIComponent(match[1]);
    }

    function persistTrialCookie(source) {
      if (!source) return;

      const cookie = `trial=${encodeURIComponent(source)}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
      document.cookie = cookie;

      if (window.location.hostname.endsWith('refrag.gg')) {
        document.cookie = `${cookie}; domain=.refrag.gg`;
      }
    }

    function errors(res) {
      state.form.errors = Object.fromEntries(
        res.data.errors.map((item) => [item.field, item.msg]),
      );
    }

    return {
      state,
      cookies,
      extractClaimTrialSource,
      persistTrialCookie,
      proxy,
    };
  },
  data() {
    return {
      Logo,
      loading: false,
    };
  },
  components: {
    DarkFull,
  },
  methods: {
    ...mapMutations(['showMessage']),
    navToRegister() {
      const trialSource = this.extractClaimTrialSource(
        this.$route.redirectedFrom,
      );
      this.persistTrialCookie(trialSource);

      this.$router.push({
        name: 'Register',
        hash: this.$route.redirectedFrom?.hash,
        query: this.$route.redirectedFrom?.query || this.$route.query,
      });
    },
    getSteamAuthURL() {
      const { join } = this.$route.query;
      if (join) return buildSteamAuthURL(`/join/${join}`, { join });
      return buildSteamAuthURL(this.returnTo());
    },
    returnTo() {
      return this.$route.query.redirect || this.$route.redirectedFrom?.fullPath || '/';
    },
    getRealm() {
      const envVar = getEnvVar();
      if (envVar == 'development') {
        return 'http://localhost:5173';
      }
      return `https://${envVar}api.refrag.gg`;
    },
    loginRedirect() {
      const { join } = this.$route.query;
      this.loading = true;
      signInStarted();
      this.$auth
        .login({
          data: this.state.form.body,
          redirect: join ? { name: 'JoinPage', params: { slug: join } } : undefined,
          remember: this.state.form.remember ? '{"name": "Redirect"}' : null,
          fetchUser: this.state.form.fetchUser,
          staySignedIn: this.state.form.staySignedIn,
        })
        .then((res) => {
          this.cookies.set('has_logged_in', true, '365d', null, '.refrag.gg');
          this.$gtm.push({
            event: 'sign_in',
            email: this.state.form.body.email,
            steam_id: res.data.data.steam_id,
            user_id: res.data.data.id,
          });
          // Identified by the /me hook in main.js; queued so it lands after that.
          trackEvent('sign_in', { method: 'email' });
          // If we have a 'code' query parameter, redirect to redeem page
          const redirectedQuery = this.$route.redirectedFrom?.query;
          if (redirectedQuery?.code) {
            this.$router.push({
              name: 'Redeem',
              query: redirectedQuery,
            });
          } else if (!join) {
            this.$router.push(this.returnTo());
          }
        })
        .then(null, (res) => {
          if (res.response?.data?.login_code_required) {
            this.state.form.codeRequired = true;
          }
          this.showMessage({
            messageType: 'error',
            messageText: res.response.data.errors[0],
          });
          this.loading = false;
        });
    },
    loginRedirectSteam() {
      this.loading = true;
    },
  },
};
</script>
